/**
 * Twilio WhatsApp Business API Service
 * Handles outbound messaging, drip campaigns, and webhook processing
 *
 * Error 63016 Fix:
 * Twilio rejects freeform (Body) messages sent outside the 24-hour customer-initiated window.
 * Solution: Use Twilio Content API — send messages with ContentSid + ContentVariables
 * instead of a plain Body string. Set TWILIO_CONTENT_SID_<TEMPLATE_KEY> env vars with
 * your approved template SIDs from Twilio Console → Content Template Builder.
 *
 * Sandbox fallback: If no ContentSid is configured, the message is sent as a Body string
 * which only works within the 24-hour window or for sandbox opt-in numbers.
 */

export interface TwilioMessageResult {
  success: boolean;
  messageSid?: string;
  status?: string;
  error?: string;
}

export interface WhatsAppTemplate {
  name: string;
  body: string;
  /** Optional: Twilio Content Template SID (HXxxxxxxxx) from Content Template Builder */
  contentSid?: string;
}

export interface DripStep {
  day: number;
  delayHours: number;
  template: string;
  message: string;
}

export interface LeadContact {
  name: string;
  phone: string;
  neetScore?: number;
  stage?: string;
  source?: string;
}

// Pre-approved WhatsApp message templates
// To fix error 63016: add contentSid for each template from Twilio Console → Content Template Builder
// OR set env vars: TWILIO_CONTENT_SID_WELCOME_MESSAGE, TWILIO_CONTENT_SID_MBBS_GUIDANCE, etc.
export const WABA_TEMPLATES: Record<string, WhatsAppTemplate> = {
  welcome_message: {
    name: 'welcome_message',
    body: 'Hi {name}! 👋 Welcome to NeetNavigator. I am your MBBS admission counselor. Congratulations on your NEET score of {score}! 🎉 We have excellent college options for you. Reply YES to explore your options.',
  },
  mbbs_guidance: {
    name: 'mbbs_guidance',
    body: 'Hi {name}! 📚 Day 1 of your MBBS journey with NeetNavigator. With a score of {score}, here is what you need to know about MBBS admissions in 2026. India has 706 medical colleges with 1.08L seats. Abroad options include Russia, Kyrgyzstan & Kazakhstan. Reply GUIDE for a detailed PDF.',
  },
  college_options: {
    name: 'college_options',
    body: 'Hi {name}! 🏫 Based on your NEET score of {score}, here are your top college options:\n\n🇮🇳 India: Government colleges via MCC counseling\n🌍 Abroad: Top MCI-approved colleges in Russia & Kyrgyzstan\n\nReply COLLEGES for the full list with fee structures.',
  },
  counseling_invite: {
    name: 'counseling_invite',
    body: 'Hi {name}! 📅 You are one step away from your MBBS dream! Book a FREE 30-minute counseling session with our expert advisor. We will discuss your best options based on your NEET score of {score}. Reply BOOK to schedule.',
  },
  application_help: {
    name: 'application_help',
    body: 'Hi {name}! 📋 Ready to apply? Here is your document checklist:\n✅ 10th & 12th Marksheets\n✅ NEET Scorecard\n✅ Passport (valid 5+ years)\n✅ 10 Passport Photos\n✅ Medical Certificate\n\nReply DOCS if you need help with any document.',
  },
  brochure_send: {
    name: 'brochure_send',
    body: 'Hi {name}! 📖 Here is our curated MBBS College Brochure 2026 for NEET {score}+ students. It covers top colleges in India and abroad with fee structures, rankings, and admission process. Reply BROCHURE to receive the PDF.',
  },
  reminder: {
    name: 'reminder',
    body: 'Hi {name}! 👋 Just checking in — have you had a chance to review the information we shared? Our counselors are available to answer any questions about your MBBS admission. Reply HELP to connect with a counselor.',
  },
  inactivity_followup: {
    name: 'inactivity_followup',
    body: 'Hi {name}! We noticed you have been inactive for a while. NEET 2026 admissions are moving fast! 🚀 Do not miss your chance to secure a seat. Reply RESUME to continue your admission journey with NeetNavigator.',
  },
  stage_interested: {
    name: 'stage_interested',
    body: 'Hi {name}! 🌟 Great news — you are now marked as Interested in our system! Your counselor will reach out shortly. In the meantime, here is our detailed brochure for NEET {score}+ students. Reply INFO for more details.',
  },
  stage_counseling_scheduled: {
    name: 'stage_counseling_scheduled',
    body: 'Hi {name}! ✅ Your counseling session is confirmed! Please be ready with:\n📋 Your NEET scorecard\n💰 Budget range\n🌍 Preferred country/state\n\nSee you soon! Reply RESCHEDULE if you need to change the time.',
  },
  payment_reminder: {
    name: 'payment_reminder',
    body: 'Hi {name}! 💳 Friendly reminder: Your application fee payment is pending. Complete your payment to secure your seat. Reply PAY for payment link or HELP for assistance.',
  },
};

// Drip campaign sequences by campaign type
export const DRIP_SEQUENCES: Record<string, DripStep[]> = {
  'neet-600-india': [
    { day: 0, delayHours: 0, template: 'welcome_message', message: 'Welcome + NEET score congratulations' },
    { day: 1, delayHours: 24, template: 'mbbs_guidance', message: 'MBBS in India — eligibility & process' },
    { day: 3, delayHours: 72, template: 'college_options', message: 'Top college options for 600+ score' },
    { day: 5, delayHours: 120, template: 'counseling_invite', message: 'Free counseling session invite' },
    { day: 7, delayHours: 168, template: 'application_help', message: 'Application documentation guide' },
  ],
  'abroad-mbbs': [
    { day: 0, delayHours: 0, template: 'welcome_message', message: 'Welcome + abroad MBBS intro' },
    { day: 1, delayHours: 24, template: 'mbbs_guidance', message: 'Russia, Kyrgyzstan, Kazakhstan overview' },
    { day: 3, delayHours: 72, template: 'college_options', message: 'MCI-approved colleges abroad' },
    { day: 5, delayHours: 120, template: 'counseling_invite', message: 'Schedule abroad counseling session' },
  ],
  'reactivation': [
    { day: 0, delayHours: 0, template: 'inactivity_followup', message: 'Re-engagement message' },
    { day: 2, delayHours: 48, template: 'brochure_send', message: 'Send updated brochure' },
    { day: 4, delayHours: 96, template: 'counseling_invite', message: 'Final counseling push' },
  ],
};

// Stage-based trigger templates
export const STAGE_TRIGGERS: Record<string, string> = {
  'New': 'welcome_message',
  'Contacted': 'mbbs_guidance',
  'Interested': 'stage_interested',
  'Counseling Scheduled': 'stage_counseling_scheduled',
  'Applied': 'application_help',
};

/**
 * Build a personalized message from a template
 */
export function buildMessage(templateKey: string, lead: LeadContact): string {
  const template = WABA_TEMPLATES[templateKey];
  if (!template) return '';
  return template.body
    .replace(/{name}/g, lead.name)
    .replace(/{score}/g, String(lead.neetScore ?? ''));
}

/**
 * Resolve ContentSid for a template key.
 * Priority: database (twilio_template_sids table) → env var → hardcoded contentSid on template.
 * Database lookup is async; use resolveContentSidAsync for server-side API routes.
 */
function resolveContentSid(templateKey: string): string | undefined {
  const envKey = `TWILIO_CONTENT_SID_${templateKey.toUpperCase()}`;
  const envVal = process.env[envKey];
  if (envVal && envVal.startsWith('HX')) return envVal;
  return WABA_TEMPLATES[templateKey]?.contentSid;
}

/**
 * Resolve ContentSid with database lookup (async, server-side only).
 * Checks Supabase twilio_template_sids table first, then whatsapp_templates.content_sid,
 * then falls back to resolveContentSid().
 */
async function resolveContentSidAsync(templateKey: string): Promise<string | undefined> {
  try {
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
    if (supabaseUrl && supabaseKey) {
      // 1. Check twilio_template_sids table first
      const res = await fetch(
        `${supabaseUrl}/rest/v1/twilio_template_sids?template_key=eq.${encodeURIComponent(templateKey)}&select=content_sid&limit=1`,
        {
          headers: {
            apikey: supabaseKey,
            Authorization: `Bearer ${supabaseKey}`,
          },
          cache: 'no-store',
        }
      );
      if (res.ok) {
        const rows = await res.json() as Array<{ content_sid: string | null }>;
        const sid = rows?.[0]?.content_sid;
        if (sid && sid.startsWith('HX')) return sid;
      }

      // 2. Fall back to whatsapp_templates.content_sid
      const res2 = await fetch(
        `${supabaseUrl}/rest/v1/whatsapp_templates?name=eq.${encodeURIComponent(templateKey)}&select=content_sid&limit=1`,
        {
          headers: {
            apikey: supabaseKey,
            Authorization: `Bearer ${supabaseKey}`,
          },
          cache: 'no-store',
        }
      );
      if (res2.ok) {
        const rows2 = await res2.json() as Array<{ content_sid: string | null }>;
        const sid2 = rows2?.[0]?.content_sid;
        if (sid2 && sid2.startsWith('HX')) return sid2;
      }
    }
  } catch {
    // fall through to env/hardcoded fallback
  }
  return resolveContentSid(templateKey);
}

/**
 * Send a WhatsApp message via Twilio REST API (server-side only).
 *
 * When contentSid is provided, uses Twilio Content API (ContentSid + ContentVariables)
 * which works outside the 24-hour window and avoids error 63016.
 *
 * When contentSid is absent, falls back to plain Body — only works within the
 * 24-hour customer-initiated window (sandbox / session messages).
 */
export async function sendWhatsAppMessage(
  to: string,
  body: string,
  contentSid?: string,
  contentVariables?: Record<string, string>,
  mediaUrl?: string
): Promise<TwilioMessageResult> {
  const accountSid = process.env.TWILIO_ACCOUNT_SID;
  const authToken = process.env.TWILIO_AUTH_TOKEN;
  const from = process.env.TWILIO_WHATSAPP_NUMBER;

  if (!accountSid || !authToken || !from) {
    return { success: false, error: 'Twilio credentials not configured' };
  }

  // Validate phone number before sending
  if (!to || !to.trim()) {
    return { success: false, error: 'Phone number is missing or empty' };
  }

  try {
    const twilioUrl = `https://api.twilio.com/2010-04-01/Accounts/${accountSid}/Messages.json`;
    const credentials = Buffer.from(`${accountSid}:${authToken}`).toString('base64');

    // Normalize phone number to E.164 (+91XXXXXXXXXX) before sending
    const normalizedTo = formatPhoneForTwilio(to);

    // Validate: must have a result and at least 10 digits
    if (!normalizedTo) {
      return { success: false, error: `Invalid phone number: "${to}" — could not normalize to E.164 format` };
    }

    // Validate the normalized number has enough digits to be a real phone number
    const digitsOnly = normalizedTo.replace(/[^\d]/g, '');
    if (digitsOnly.length < 10) {
      return { success: false, error: `Invalid phone number: "${to}" — only ${digitsOnly.length} digit(s) found after normalization` };
    }

    const toWhatsApp = normalizedTo.startsWith('whatsapp:') ? normalizedTo : `whatsapp:${normalizedTo}`;
    const fromWhatsApp = from.startsWith('whatsapp:') ? from : `whatsapp:${from}`;

    const formData = new URLSearchParams({
      To: toWhatsApp,
      From: fromWhatsApp,
    });

    if (contentSid) {
      // Use Twilio Content API — avoids error 63016 outside 24-hour window
      formData.append('ContentSid', contentSid);
      // Always send ContentVariables when using ContentSid — even if empty, send "{}" so
      // Twilio knows to render the approved template rather than falling back to Body text
      const vars = (contentVariables && Object.keys(contentVariables).length > 0)
        ? contentVariables
        : {};
      formData.append('ContentVariables', JSON.stringify(vars));
    } else {
      // Fallback: plain body — only works within 24-hour session window
      if (body) formData.append('Body', body);
      if (mediaUrl) formData.append('MediaUrl0', mediaUrl);
    }

    // ── DEBUG: log exact payload being sent to Twilio ──────────────────────
    const payloadDebug: Record<string, string> = {};
    formData.forEach((v, k) => { payloadDebug[k] = k === 'ContentVariables' ? v : v; });
    console.log('[twilio] sendWhatsAppMessage payload:', JSON.stringify({
      url: twilioUrl,
      To: toWhatsApp,
      From: fromWhatsApp,
      ContentSid: contentSid ?? null,
      ContentVariables: contentVariables ?? null,
      hasBody: !contentSid,
    }));

    const response = await fetch(twilioUrl, {
      method: 'POST',
      headers: {
        Authorization: `Basic ${credentials}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
    });

    const data = await response.json() as { sid?: string; status?: string; message?: string; code?: number };

    console.log('[twilio] Twilio API response:', JSON.stringify({ status: response.status, sid: data.sid, twilioStatus: data.status, error: data.message, code: data.code }));

    if (!response.ok) {
      const errMsg = data.message ?? 'Twilio API error';
      // Provide actionable guidance for error 63016
      if (data.code === 63016) {
        return {
          success: false,
          error: `Error 63016: Outside 24-hour window. Configure a Twilio Content Template SID (HXxxxxxxxx) for this template to send outside the session window. See Twilio Console → Content Template Builder.`,
        };
      }
      return { success: false, error: errMsg };
    }

    return { success: true, messageSid: data.sid, status: data.status };
  } catch (err) {
    const error = err instanceof Error ? err.message : 'Unknown error';
    return { success: false, error };
  }
}

/**
 * Send a template-based WhatsApp message to a lead.
 * Automatically uses ContentSid if configured via database, env vars, or template definition.
 */
export async function sendTemplateMessage(
  templateKey: string,
  lead: LeadContact
): Promise<TwilioMessageResult> {
  // Validate phone before doing any work
  if (!lead.phone || !lead.phone.trim()) {
    return { success: false, error: `Lead "${lead.name}" has no phone number — skipping` };
  }

  // Pre-validate: normalize and check digit count before proceeding
  const normalizedPhone = formatPhoneForTwilio(lead.phone);
  const digitsOnly = normalizedPhone.replace(/[^\d]/g, '');
  if (!normalizedPhone || digitsOnly.length < 10) {
    return { success: false, error: `Lead "${lead.name}" has an invalid phone number "${lead.phone}" — skipping` };
  }

  let body = buildMessage(templateKey, lead);
  let dbContentSid: string | undefined;

  // If not found in static WABA_TEMPLATES, look up body (and content_sid) from Supabase whatsapp_templates
  if (!body) {
    try {
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
      if (supabaseUrl && supabaseKey) {
        // Build a list of name variants to try (handles mismatches like "Welcome _1" vs "welcome_1")
        const nameVariants = Array.from(new Set([
          templateKey,
          templateKey.toLowerCase(),
          templateKey.replace(/\s+/g, '_'),
          templateKey.toLowerCase().replace(/\s+/g, '_'),
          templateKey.replace(/_/g, ' '),
          templateKey.toLowerCase().replace(/_/g, ' '),
          templateKey.replace(/\s_/g, '_').replace(/__+/g, '_'),
          templateKey.toLowerCase().replace(/\s_/g, '_').replace(/__+/g, '_'),
        ]));

        let row: { body: string; content_sid: string | null; sample_values: Record<string, string> | null } | null = null;

        for (const variant of nameVariants) {
          const res = await fetch(
            `${supabaseUrl}/rest/v1/whatsapp_templates?name=eq.${encodeURIComponent(variant)}&select=body,content_sid,sample_values&limit=1`,
            {
              headers: {
                apikey: supabaseKey,
                Authorization: `Bearer ${supabaseKey}`,
              },
              cache: 'no-store',
            }
          );
          if (res.ok) {
            const rows = await res.json() as Array<{ body: string; content_sid: string | null; sample_values: Record<string, string> | null }>;
            if (rows?.[0]?.body) {
              row = rows[0];
              console.log('[twilio] DB whatsapp_templates matched variant:', variant, 'for key:', templateKey);
              break;
            }
          }
        }

        console.log('[twilio] DB whatsapp_templates lookup for', templateKey, '→', JSON.stringify({ found: !!row, content_sid: row?.content_sid }));

        if (row?.body) {
          const rawBody = row.body;
          body = rawBody
            .replace(/\{\{name\}\}/gi, lead.name)
            .replace(/\{\{1\}\}/g, lead.name)
            .replace(/\{\{score\}\}/gi, String(lead.neetScore ?? ''))
            .replace(/\{\{2\}\}/g, String(lead.neetScore ?? ''))
            .replace(/\{\{stage\}\}/gi, lead.stage ?? '')
            .replace(/\{\{3\}\}/g, lead.stage ?? '');

          // Capture content_sid stored directly on the template row
          if (row.content_sid && row.content_sid.startsWith('HX')) {
            dbContentSid = row.content_sid;
          }

          // Build contentVariables by scanning ALL {{N}} placeholders in the raw body
          const varIndices = new Set<string>();
          const varPattern = /\{\{(\d+)\}\}/g;
          let match: RegExpExecArray | null;
          while ((match = varPattern.exec(rawBody)) !== null) {
            varIndices.add(match[1]);
          }

          // Map each index to the appropriate lead field value
          const resolvedVars: Record<string, string> = {};
          varIndices.forEach((idx) => {
            switch (idx) {
              case '1': resolvedVars[idx] = lead.name; break;
              case '2': resolvedVars[idx] = String(lead.neetScore ?? ''); break;
              case '3': resolvedVars[idx] = lead.stage ?? ''; break;
              case '4': resolvedVars[idx] = lead.source ?? ''; break;
              default:  resolvedVars[idx] = ''; break;
            }
          });

          // Override with sample_values for any index not already mapped to a lead field
          if (row.sample_values) {
            Object.entries(row.sample_values).forEach(([k, v]) => {
              if (!resolvedVars[k]) resolvedVars[k] = v;
            });
          }

          // Resolve the best ContentSid: twilio_template_sids table > whatsapp_templates.content_sid > env var
          const resolvedSid = await resolveContentSidAsync(templateKey) ?? dbContentSid;

          console.log('[twilio] DB path → resolvedSid:', resolvedSid, 'resolvedVars:', JSON.stringify(resolvedVars));

          if (!body) return { success: false, error: `Template "${templateKey}" not found` };

          // Use normalizedPhone (already validated above) instead of raw lead.phone
          return sendWhatsAppMessage(normalizedPhone, body, resolvedSid, resolvedVars);
        }
      }
    } catch (e) {
      console.error('[twilio] DB lookup error:', e);
      // fall through to error below
    }
  }

  if (!body) return { success: false, error: `Template "${templateKey}" not found` };

  const contentSid = await resolveContentSidAsync(templateKey);

  // Build content variables for Twilio Content API (variable names must match template)
  const contentVariables: Record<string, string> = {
    '1': lead.name,
    '2': String(lead.neetScore ?? ''),
    '3': lead.stage ?? '',
    '4': lead.source ?? '',
  };

  console.log('[twilio] static path → contentSid:', contentSid, 'contentVariables:', JSON.stringify(contentVariables));

  return sendWhatsAppMessage(normalizedPhone, body, contentSid, contentVariables);
}

/**
 * Trigger stage-based WhatsApp message when lead stage changes
 */
export async function triggerStageMessage(
  lead: LeadContact,
  newStage: string
): Promise<TwilioMessageResult> {
  const templateKey = STAGE_TRIGGERS[newStage];
  if (!templateKey) return { success: false, error: `No trigger for stage: ${newStage}` };
  return sendTemplateMessage(templateKey, lead);
}

/**
 * Normalize Indian phone numbers to E.164 format (+91XXXXXXXXXX).
 * Handles common variations like 0, +91, 91, 0091, etc.
 * Returns empty string for invalid/empty input.
 */
export function formatPhoneForTwilio(phone: string): string {
  if (!phone || !phone.trim()) return '';

  // Strip all non-digit characters
  let cleaned = phone.replace(/[^\d]/g, '');

  // If no digits at all, return empty
  if (!cleaned) return '';

  // Remove leading zeros (e.g. 0091... or 091...)
  cleaned = cleaned.replace(/^0+/, '');

  // If nothing left after stripping zeros, return empty
  if (!cleaned) return '';

  // If starts with 91 and total length is 12, it's already +91XXXXXXXXXX
  if (cleaned.startsWith('91') && cleaned.length === 12) {
    return `+${cleaned}`;
  }

  // If it's a 10-digit number, prepend 91
  if (cleaned.length === 10) {
    return `+91${cleaned}`;
  }

  // If it's 11 digits starting with 91 (e.g. 91XXXXXXXXXX without leading +)
  if (cleaned.startsWith('91') && cleaned.length === 11) {
    // Likely 91 + 9-digit number — just prepend +
    return `+${cleaned}`;
  }

  // Fallback: prepend + and hope for the best
  return `+${cleaned}`;
}
